import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Box,
} from '@material-ui/core';

import { HeatMapData } from '../types';

interface Props {
  data: HeatMapData[];
  period: 'week' | 'month';
}

const WEEK_COLUMNS = ['t0', 't1', 't2', 't3', 't4', 't5', 't6'] as const;

const MONTH_COLUMNS = ['Added', 'Modified', 'Deleted', 'Total'] as const;

const getColor = (value: number, max: number) => {
  if (!value) return '#F8FAFC';

  const opacity = 0.15 + (value / max) * 0.85;

  return `rgba(79,70,229,${opacity})`;
};

const getTextColor = (value: number, max: number) => (value / max > 0.45 ? '#fff' : '#111827');

const HeatMap = ({ data, period }: Props) => {
  //console.log('data in heatmap compo', data);
  const columns = period === 'week' ? WEEK_COLUMNS : MONTH_COLUMNS;

  const max = Math.max(
    1,
    ...data.flatMap(row =>
      period === 'week'
        ? [
            row.t0 ?? 0,
            row.t1 ?? 0,
            row.t2 ?? 0,
            row.t3 ?? 0,
            row.t4 ?? 0,
            row.t5 ?? 0,
            row.t6 ?? 0,
          ]
        : [row.added ?? 0, row.modified ?? 0, row.deleted ?? 0, row.total ?? 0],
    ),
  );

  const getValue = (row: HeatMapData, column: string) => {
    switch (column) {
      case 't0':
        return row.t0 ?? 0;
      case 't1':
        return row.t1 ?? 0;
      case 't2':
        return row.t2 ?? 0;
      case 't3':
        return row.t3 ?? 0;
      case 't4':
        return row.t4 ?? 0;
      case 't5':
        return row.t5 ?? 0;
      case 't6':
        return row.t6 ?? 0;
      case 'Added':
        return row.added ?? 0;
      case 'Modified':
        return row.modified ?? 0;
      case 'Deleted':
        return row.deleted ?? 0;
      case 'Total':
        return row.total ?? 0;
      default:
        return 0;
    }
  };
  const classes = useStyles();
  return (
    <Card
      elevation={3}
      style={{
        borderRadius: 16,
      }}>
      <CardContent>
        <Typography
          variant="h6"
          style={{
            fontWeight: 700,
            marginBottom: 18,
          }}>
          Activity Heatmap ({period === 'week' ? 'Last 7 Days' : 'Last 30 Days'})
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  fontWeight: 700,
                }}>
                Field Name
              </TableCell>

              {columns.map(col => (
                <TableCell
                  key={col}
                  align="center"
                  style={{
                    fontWeight: 700,
                  }}>
                  {col.toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map(row => (
              <TableRow key={row.fieldName} hover>
                <TableCell
                  style={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}>
                  {row.fieldName}
                </TableCell>

                {columns.map(col => {
                  const value = getValue(row, col);
                  const weekData =
                    period === 'week' ? row.week[col as keyof typeof row.week] : null;
                  return (
                    <Tooltip
                      arrow
                      key={col}
                      classes={{
                          tooltip: classes.tooltip,
                          arrow: classes.arrow,
                      }}
                      enterDelay={150}
                      title={
                        <Box>
                          <Typography
                            style={{
                              fontWeight: 700,
                              marginBottom: 6,
                            }}>
                            {row.fieldName}
                          </Typography>

                          {period === 'week' && weekData && (
                            <>
                              <div>Day : {col.toUpperCase()}</div>

                              <div>Total : {weekData.total.toLocaleString()}</div>

                              <div style={{ color: '#22C55E' }}>
                                Added : {weekData.added.toLocaleString()}
                              </div>

                              <div style={{ color: '#3B82F6' }}>
                                Modified : {weekData.modified.toLocaleString()}
                              </div>

                              <div style={{ color: '#EF4444' }}>
                                Deleted : {weekData.deleted.toLocaleString()}
                              </div>
                            </>
                          )}

                          {period === 'month' && (
                            <>
                              <div>Total : {row.total.toLocaleString()}</div>

                              <div style={{ color: '#22C55E' }}>
                                Added : {row.added.toLocaleString()}
                              </div>

                              <div style={{ color: '#3B82F6' }}>
                                Modified : {row.modified.toLocaleString()}
                              </div>

                              <div style={{ color: '#EF4444' }}>
                                Deleted : {row.deleted.toLocaleString()}
                              </div>
                            </>
                          )}
                        </Box>
                      }>
                      <TableCell
                        align="center"
                        style={{
                          background: getColor(value, max),
                          color: getTextColor(value, max),
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: '.2s',
                        }}>
                        {value.toLocaleString()}
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Legend */}

        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <Typography variant="caption" color="textSecondary">
            Low Activity
          </Typography>

          {[0.15, 0.28, 0.4, 0.55, 0.7, 0.85, 1].map(level => (
            <Box
              key={level}
              mx={0.3}
              width={32}
              height={12}
              borderRadius={2}
              bgcolor={`rgba(79,70,229,${level})`}
            />
          ))}

          <Typography variant="caption" color="textSecondary">
            High Activity
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default HeatMap;

import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  tooltip: {
    background: "#fff",
    color: "#111827",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "12px 14px",
    boxShadow: "0 8px 24px rgba(0,0,0,.18)",
    fontSize: 12,
    maxWidth: 220,
  },
  arrow: {
    color: "#fff",
  },
});
